import { useMutation, useQuery } from '@apollo/client'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { authErrorCode, isWrongCode, nhost } from '~/helpers'
import { RESET, SET_REQUIRED, TEAM, useTwoFactor } from './twoFactor'
import { TwoFactorSetup } from './TwoFactorSetup'

interface Member {
  id: string
  displayName: string | null
  email: string
  activeMfaType: string | null
}

/**
 * "Mein Konto" → two-factor sign-in: one's own, and for admins the team's —
 * who has it, a reset for a lost phone, and the switch that makes it
 * mandatory for admins.
 */
export const TwoFactorCard = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const mfa = useTwoFactor()
  const team = useQuery<{ users: Member[] }>(TEAM, {
    skip: !mfa.isAdmin,
    fetchPolicy: 'cache-and-network',
  })
  const [setRequired, { loading: switching }] = useMutation(SET_REQUIRED)
  const [reset] = useMutation(RESET)
  const [disabling, setDisabling] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resetting, setResetting] = useState<Member | null>(null)
  const me = nhost.getUserSession()?.user?.id

  const changed = () => {
    mfa.refetch()
    if (mfa.isAdmin) team.refetch()
  }

  const disable = async () => {
    setError(null)
    try {
      await nhost.auth.verifyChangeUserMfa({ code: code.trim(), activeMfaType: '' })
      notify('account.mfa.disabled', { type: 'info' })
      setDisabling(false)
      setCode('')
      changed()
    } catch (e: any) {
      setError(isWrongCode(e) ? translate('account.mfa.invalid') : (authErrorCode(e) ?? e.message))
    }
  }

  const name = (m: Member) => m.displayName || m.email

  return (
    <Card variant='outlined' data-testid='mfa-card'>
      <CardContent>
        <Stack direction='row' spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
          <VerifiedUserIcon />
          <Typography variant='h6'>{translate('account.mfa.title')}</Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2, maxWidth: 820 }}>
          {translate('account.mfa.intro')}
        </Typography>

        {mfa.loaded &&
          (mfa.active ? (
            <>
              <Alert severity='success' sx={{ mb: 1.5 }} data-testid='mfa-on'>
                {translate('account.mfa.on')}
              </Alert>
              {mfa.required && mfa.isAdmin ? (
                <Typography variant='body2' color='text.secondary'>
                  {translate('account.mfa.locked')}
                </Typography>
              ) : (
                <Button onClick={() => setDisabling(true)}>
                  {translate('account.mfa.disable')}
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography variant='body2' sx={{ mb: 1.5 }} data-testid='mfa-off'>
                {translate('account.mfa.off')}
              </Typography>
              <TwoFactorSetup onDone={changed} />
            </>
          ))}

        {mfa.isAdmin && team.data && (
          <Box sx={{ mt: 3 }} data-testid='mfa-team'>
            <Typography variant='subtitle1'>{translate('account.mfa.team')}</Typography>
            <Table size='small' sx={{ mb: 1.5, maxWidth: 720 }}>
              <TableBody>
                {team.data.users.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{name(m)}</TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        variant='outlined'
                        color={m.activeMfaType ? 'success' : 'default'}
                        label={translate(
                          m.activeMfaType ? 'account.mfa.status_on' : 'account.mfa.status_off'
                        )}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      {m.activeMfaType && m.id !== me && (
                        <Button size='small' onClick={() => setResetting(m)}>
                          {translate('account.mfa.reset')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <FormControlLabel
              control={
                <Switch
                  checked={mfa.required}
                  disabled={switching || (!mfa.required && !mfa.active)}
                  onChange={async (e) => {
                    await setRequired({ variables: { on: e.target.checked } })
                    mfa.refetch()
                  }}
                  slotProps={{ input: { 'aria-label': translate('account.mfa.require') } }}
                />
              }
              label={translate('account.mfa.require')}
            />
            <Typography variant='body2' color='text.secondary'>
              {translate(
                !mfa.required && !mfa.active
                  ? 'account.mfa.require_own_first'
                  : 'account.mfa.require_hint'
              )}
            </Typography>
          </Box>
        )}
      </CardContent>

      <Dialog open={disabling} onClose={() => setDisabling(false)}>
        <DialogTitle>{translate('account.mfa.disable_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {translate('account.mfa.disable_hint')}
          </DialogContentText>
          {error && (
            <Alert severity='error' sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          <TextField
            size='small'
            label={translate('account.mfa.code')}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'one-time-code' } }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisabling(false)}>{translate('ra.action.cancel')}</Button>
          <Button variant='contained' disabled={code.length !== 6} onClick={disable}>
            {translate('account.mfa.disable')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!resetting} onClose={() => setResetting(null)}>
        <DialogTitle>
          {resetting && translate('account.mfa.reset_title', { name: name(resetting) })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {resetting && translate('account.mfa.reset_hint', { name: name(resetting) })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetting(null)}>{translate('ra.action.cancel')}</Button>
          <Button
            variant='contained'
            color='warning'
            onClick={async () => {
              const who = resetting as Member
              setResetting(null)
              await reset({ variables: { id: who.id } })
              notify('account.mfa.reset_done', { type: 'info', messageArgs: { name: name(who) } })
              changed()
            }}
          >
            {translate('account.mfa.reset')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default TwoFactorCard
